import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector, useDispatch} from 'react-redux';

import {useInflight} from 'src/hooks/redux';
import {getJobScoreCards, getInterviewFlow} from 'src/action-creators/crm';

import {
  selectApplicantInformation,
  selectJobInterviewFlow,
  selectJobScoreCards,
  selectUserInfo,
  selectHasCrmPermission,
} from 'src/selectors/crm';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';

import SingleEvaluation from 'src/components/crm/lib/candidate-evaluation/single-evaluation';

import * as reduxApi from 'src/utils/redux-api-v2';

import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import type {
  Stage,
  JOB_SCORE_CARDS,
  InterviewReviewItem,
  AssessmentReportItem,
  StepCandidateForFeedback,
} from 'src/types/crm';

import css from './feedback.css';


type ApplicantFeedbacksProps = {
  jobId: number;
  reloadCandidateData: () => void;
  stepCandidateId?: number;
  fromFeedbackPanel?: boolean;
};

type StepCandidateFeedbackWrapperProps = {
  stepCandidate: StepCandidateForFeedback;
  jobStages: Stage[];
  scorecard: JOB_SCORE_CARDS | null;
  reloadCandidateData: () => void;
  jobId: number;
};

type CombinedReviewItem = InterviewReviewItem & {
  testStart?: number;
  testEnd?: number;
  reportUrl?: string;
  percentage?: number;
  metadata?: string;
};

const ApplicantFeedbacks = ({
  jobId,
  reloadCandidateData,
  stepCandidateId,
  fromFeedbackPanel = false,
}: ApplicantFeedbacksProps) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();

  const applicantDetails = useSelector(selectApplicantInformation);
  const [loadingPreviousJobFeedback, setLoadingPreviousJobFeedback] =
    React.useState(false);
  const [previousJobFeedback, setPreviousJobFeedback] =
    React.useState<any>(null);
  const [accordionCollapseState, setAccordionCollapseState] = React.useState({
    currentJob: true,
    pastJob: false,
  });

  const [interviewReviews, setInterviewReviews] = React.useState([]);
  const [assessmentReports, setAssessmentReports] = React.useState([]);
  const [isApiDataLoading, setIsApiDataLoading] = React.useState(false);

  const currentApplication = applicantDetails?.applications?.find(
    (app: any) =>
      app?.jobId === jobId && !app.duplicate && app.active && app.jobId > 0,
  );

  const isInterviewFlowDataLoading = useInflight(
    getInterviewFlow(jobId.toString(), applicantDetails?.id?.toString()),
  );
  const interviewFlowData = useSelector(selectJobInterviewFlow);
  const jobStages = interviewFlowData?.stages ?? [];

  const {stepCandidates = []} = currentApplication ?? {};

  const hasFeedbacks = React.useMemo(() => {
    if (fromFeedbackPanel) {
      return interviewReviews.length > 0 || assessmentReports.length > 0;
    }

    if (!stepCandidates || stepCandidates.length === 0) {
      return false;
    }

    return stepCandidates.some((stepCandidate) => {
      // @ts-expect-error - TS2339 - Property 'interviewReviews' does not exist on type 'ApplicationStepCandidate'. | TS2339 - Property 'assessmentCandidate' does not exist on type 'ApplicationStepCandidate'.
      const {interviewReviews = [], assessmentCandidate} = stepCandidate || {};
      const assesmentReports = assessmentCandidate
        ? [assessmentCandidate.report]
        : [];
      return (
        interviewReviews.length > 0 || assesmentReports.some((report) => report)
      );
    });
  }, [stepCandidates, fromFeedbackPanel, interviewReviews, assessmentReports]);

  const isJobScoreCardDataLoading = useInflight(getJobScoreCards(jobId));
  const jobScoreCardData = useSelector(selectJobScoreCards);

  const toggleAccordionCollapseState = (key: string) => {
    setAccordionCollapseState((prev) => ({
      ...prev,
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ currentJob: boolean; pastJob: boolean; }'.
      [key]: !accordionCollapseState[key],
    }));
  };

  // Fetch data from APIs when fromFeedbackPanel is true
  React.useEffect(() => {
    let isCancelled = false;

    if (fromFeedbackPanel && stepCandidateId) {
      setIsApiDataLoading(true);

      const fetchInterviewReviews = dispatch(
        reduxApi.get(
          `api/all-interview-reviews?interviewId=${stepCandidateId}`,
          {},
          {apiPath: '/trmapi/'},
        ),
      )
        .then((data) => {
          if (!isCancelled) {
            setInterviewReviews(data || []);
          }
        })
        .catch(() => {
          if (!isCancelled) {
            setInterviewReviews([]);
          }
        });

      const fetchAssessmentReports = dispatch(
        reduxApi.get(
          `api/all-assessment-reports?interviewId=${stepCandidateId}`,
          {},
          {apiPath: '/trmapi/'},
        ),
      )
        .then((data) => {
          if (!isCancelled) {
            setAssessmentReports(data || []);
          }
        })
        .catch(() => {
          if (!isCancelled) {
            setAssessmentReports([]);
          }
        });

      Promise.all([fetchInterviewReviews, fetchAssessmentReports]).finally(
        () => {
          if (!isCancelled) {
            setIsApiDataLoading(false);
          }
        },
      );
    }

    return () => {
      isCancelled = true;
    };
  }, [fromFeedbackPanel, stepCandidateId, dispatch]);

  React.useEffect(() => {
    if (!jobId || !applicantDetails?.id) {
      return;
    }

    const fetchApplicantPreviousFeedback = async () => {
      setLoadingPreviousJobFeedback(true);
      await dispatch(
        reduxApi.get(
          `api/relogged-feedback?jobId=${jobId}&candidateId=${applicantDetails?.id}`,
          {},
          {apiPath: '/trmapi/'},
        ),
      )
        .then((data) => setPreviousJobFeedback(data))
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoadingPreviousJobFeedback(false);
        });
    };

    fetchApplicantPreviousFeedback();
  }, [jobId, applicantDetails?.id]);

  return (
    <div className={css.applicantFeedbackContainer}>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader
          // @ts-expect-error - TS2322 - Type '{ children: Element[]; onClick: () => void; className: string; }' is not assignable to type 'IntrinsicAttributes & CardChildProps'.
          onClick={() => toggleAccordionCollapseState('currentJob')}
          className={css.accordionCardHeader}
        >
          <CardTitle>{t('From_Current_job', 'From Current job')}</CardTitle>
          <CardActions>
            <Button
              size="small"
              type="ghost"
              iconLeftName={
                accordionCollapseState.currentJob
                  ? 'chevron-up'
                  : 'chevron-down'
              }
              onClick={() => toggleAccordionCollapseState('currentJob')}
            />
          </CardActions>
        </CardHeader>
        {accordionCollapseState.currentJob ? (
          <CardContent className={css.cardContent}>
            {(
              fromFeedbackPanel
                ? isApiDataLoading
                : isInterviewFlowDataLoading || isJobScoreCardDataLoading
            ) ? (
              <Shimmer width="100%" height="100px" />
            ) : hasFeedbacks ? (
              <div className={css.currentJobCardContentContainer}>
                {fromFeedbackPanel ? (
                  <ApiFeedbacksRenderer
                    interviewReviews={interviewReviews}
                    assessmentReports={assessmentReports}
                    jobStages={jobStages}
                    scorecard={jobScoreCardData}
                    reloadCandidateData={reloadCandidateData}
                    jobId={jobId}
                  />
                ) : (
                  stepCandidates?.map((stepCandidate) => (
                    <StepCandidateFeedbackWrapper
                      key={stepCandidate.id}
                      stepCandidate={stepCandidate}
                      jobStages={jobStages}
                      scorecard={jobScoreCardData}
                      reloadCandidateData={reloadCandidateData}
                      jobId={jobId}
                    />
                  ))
                )}
              </div>
            ) : (
              <BodyMedium>
                {t(
                  'No_feedbacks_has_been_submitted_for_this_candidate_yet',
                  'No feedbacks has been submitted for this candidate yet',
                )}
              </BodyMedium>
            )}
          </CardContent>
        ) : null}
      </Card>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader
          // @ts-expect-error - TS2322 - Type '{ children: Element[]; onClick: () => void; className: string; }' is not assignable to type 'IntrinsicAttributes & CardChildProps'.
          onClick={() => toggleAccordionCollapseState('pastJob')}
          className={css.accordionCardHeader}
        >
          <CardTitle>{t('From_Past_job', 'From Past job')}</CardTitle>
          <CardActions>
            <Button
              size="small"
              type="ghost"
              iconLeftName={
                accordionCollapseState.pastJob ? 'chevron-up' : 'chevron-down'
              }
              onClick={() => toggleAccordionCollapseState('pastJob')}
            />
          </CardActions>
        </CardHeader>
        {accordionCollapseState.pastJob ? (
          <CardContent className={css.cardContent}>
            {loadingPreviousJobFeedback ? (
              <CircularLoader />
            ) : previousJobFeedback ? (
              <iframe
                title="resume-iframe"
                className="doc"
                src={previousJobFeedback?.publicUrl}
                height="1000px"
                width="100%"
              />
            ) : (
              <BodyMedium>
                {t(
                  'Previous_job\'s_feedback_is_not_available',
                  'Previous job\'s feedback is not available',
                )}
              </BodyMedium>
            )}
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
};

export default ApplicantFeedbacks;

type ApiFeedbacksRendererProps = {
  interviewReviews: InterviewReviewItem[];
  assessmentReports: AssessmentReportItem[];
  jobStages: Stage[];
  scorecard: JOB_SCORE_CARDS | null;
  reloadCandidateData: () => void;
  jobId: number;
};

// Component to render feedbacks from API data
const ApiFeedbacksRenderer = ({
  interviewReviews,
  assessmentReports,
  jobStages,
  scorecard,
  reloadCandidateData,
  jobId,
}: ApiFeedbacksRendererProps) => {
  const allReviews: (InterviewReviewItem | AssessmentReportItem)[] = [
    ...interviewReviews,
    ...assessmentReports.map((report) => ({
      ...report,
      platform: report.platform || 'assessment',
    })),
  ];

  if (allReviews.length === 0) {
    return null;
  }

  return (
    <>
      {allReviews
        .sort((a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0))
        .map((review) => {
          const stage = jobStages?.find((stage) => {
            return stage.id === review.stageId;
          });

          const stepCandidateData = {
            id:
              (review as InterviewReviewItem).interviewCandidateId ||
              review.stepCandidateId,
            stepId: review.stepId,
            stageId: review.stageId,
            jobId: review.jobId || jobId,
            candidateId: review.candidateId,
          };
          return (
            // @ts-expect-error - TS2741 - Property 'scorecardConfigData' is missing in type
            <SingleEvaluation
              key={review.id}
              review={review}
              stepCandidate={stepCandidateData}
              scorecard={scorecard}
              stage={stage}
              onUpdate={reloadCandidateData}
              jobId={jobId}
            />
          );
        })}
    </>
  );
};

const StepCandidateFeedbackWrapper = ({
  stepCandidate,
  jobStages,
  scorecard,
  reloadCandidateData,
  jobId,
}: StepCandidateFeedbackWrapperProps) => {
  const user = useSelector(selectUserInfo);
  const {user: userInfo} = user ?? {};

  const {
    interviewReviews = [],
    assessmentCandidate,
    stepId,
    stageId,
  } = stepCandidate || {};

  const assessmentReports: CombinedReviewItem[] = assessmentCandidate
    ? [
        {
          ...assessmentCandidate.report,
          id: assessmentCandidate.id,
          stepId,
          stageId,
          createdOn: assessmentCandidate.report?.testEnd ?? 0,
        },
      ]
    : [];

  let finalInterviewReviews: CombinedReviewItem[] = [
    ...interviewReviews,
    ...assessmentReports,
  ];

  const viewOthersFeedback = selectHasCrmPermission(
    user,
    CRM_PERMISSIONS.VIEW_OTHERS_FEEDBACK,
  );
  if (!viewOthersFeedback) {
    finalInterviewReviews = finalInterviewReviews.filter(
      (review) => review.reviewerId === userInfo?.id,
    );
  }

  if (finalInterviewReviews.length === 0) {
    return null;
  }

  return (
    <>
      {finalInterviewReviews
        .sort((a, b) => a.createdOn - b.createdOn)
        .map((review) => {
          const stage = jobStages?.find((stage) => {
            return stage.id === review.stageId;
          });
          return (
            // @ts-expect-error - TS2741 - Property 'scorecardConfigData' is missing in type '{ key: any; review: any; stepCandidate: any; scorecard: any; stage: any; onUpdate: any; jobId: any; }' but required in type '{ review: any; stepCandidate: any; stage: any; scorecard: any; scorecardConfigData: any; hideDelete?: boolean | undefined; jobId: any; onUpdate: any; }'.
            <SingleEvaluation
              key={review.id}
              review={review}
              stepCandidate={stepCandidate}
              scorecard={scorecard}
              stage={stage}
              onUpdate={reloadCandidateData}
              jobId={jobId}
            />
          );
        })}
    </>
  );
};
